/* eslint-disable react/prop-types */
import colorTokens from '../../../constants/design_system/colors';
import { TaskStatus } from '../../../types/TaskStatus';
import { flagWidth } from '../../TaskTable/helpers/helper';
import StatusLabel, { StatusComponentType } from '../../UI/StatusLabel';
import FlagDefault from '../../../assets/icons/flag-default.svg?react';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HolderOutlined } from '@ant-design/icons';

export const TaskStatusesGroup: React.FC<{
  statuses: TaskStatus[];
  selectedStatus: TaskStatus;
  selectStatus: (status: TaskStatus) => void;
  updateStatuses: (statuses: TaskStatus[]) => void;
}> = ({ statuses, selectedStatus, selectStatus, updateStatuses }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  function onDragStart(event: DragStartEvent) {}

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    const indexWhereDropeed = statuses.findIndex((s) => s.id === +overId);
    const indexFrom = statuses.findIndex((s) => s.id === +activeId);

    const orderArray = arrayMove(statuses, indexFrom, indexWhereDropeed);

    const newStatuses = orderArray.map((status, index) => {
      return {
        ...status,
        orderIndex: index,
      };
    });

    updateStatuses(newStatuses);
  }

  function onDragOver(event: DragOverEvent) {}

  const sortedStatuses = statuses.sort((a, b) => (a.orderIndex ?? a.id) - (b.orderIndex ?? a.id));

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragOver={onDragOver}>
      <SortableContext items={statuses.map((status) => status.id)}>
        {sortedStatuses.map((status: TaskStatus) => (
          <TaskStatusSortable
            key={status.id}
            status={status}
            isSelected={selectedStatus === status}
            selectStatus={selectStatus}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

export const TaskStatusSortable: React.FC<{
  status: TaskStatus;
  isSelected: boolean;
  selectStatus: (status: TaskStatus) => void;
}> = ({ status, isSelected, selectStatus }) => {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: status.id,
    data: {
      type: 'TaskStatus',
      status,
    },
  });

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  };

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, backgroundColor: isSelected ? colorTokens.neutral[300] : undefined }}
      {...attributes}
      {...listeners}
      className="flex items-center p-1 my-0.5 cursor-pointer hover:bg-neutral-300 rounded"
      key={status.id}
      onClick={() => selectStatus(status)}
    >
      <HolderOutlined className="mr-2 ml-1" />
      <span className="cursor-text">
        <StatusLabel
          color={status.color}
          title={status.title}
          group={status.group}
          component={StatusComponentType.EditStatusesModal}
        />
      </span>
      <div className="ml-auto" />
      {status.default && <FlagDefault style={{ width: flagWidth }} />}
    </div>
  );
};
